import { batch, createMemo, createSignal, For, on, Show, createEffect } from 'solid-js'
import { activeTool, editingTextId, marquee } from '../editor.ts'
import { nodeCorners, nodesBounds } from '../scene/geometry.ts'
import {
  abortCommit,
  beginCommit,
  endCommit,
  scene,
  selectedNodes,
  selection,
  updateNode,
} from '../scene/store.ts'
import {
  angleTo,
  HANDLES,
  mapRect,
  PIVOT_SNAPS,
  pivotFromWorld,
  pivotToWorld,
  resizeRect,
  resizeRotated,
  rotateAbout,
  snapAngle,
  type Handle,
} from '../scene/transform.ts'
import type { Point, Rect } from '../scene/types.ts'
import { clientToWorld } from './viewportRef.ts'
import { worldRectToScreen, worldToScreen } from './camera.ts'
import { startDrag } from '../util/drag.ts'

const HANDLE_SIZE = 9
const ROTATE_OFFSET = 22
const PIVOT_RADIUS = 6
/** Screen pixels within which the pivot snaps to the center or a corner. */
const PIVOT_SNAP = 10

const CURSORS: Record<Handle, string> = {
  nw: 'nwse-resize', se: 'nwse-resize',
  ne: 'nesw-resize', sw: 'nesw-resize',
  n: 'ns-resize', s: 'ns-resize',
  e: 'ew-resize', w: 'ew-resize',
}

function mid(a: Point, b: Point): Point {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
}

/** Screen position of each handle, derived from the frame's four corners so
 *  that a rotated single selection gets rotated handles for free. */
function handlePositions(c: readonly Point[]): Record<Handle, Point> {
  const [nw, ne, se, sw] = c as [Point, Point, Point, Point]
  return {
    nw, ne, se, sw,
    n: mid(nw, ne),
    e: mid(ne, se),
    s: mid(se, sw),
    w: mid(sw, nw),
  }
}

function startResize(handle: Handle, e: PointerEvent): void {
  e.stopPropagation()
  e.preventDefault()

  const nodes = selectedNodes()
  if (nodes.length === 0) return

  const origs = nodes.map((n) => ({
    id: n.id,
    rect: { x: n.x, y: n.y, w: n.w, h: n.h } as Rect,
    rotation: n.rotation,
    isImage: n.type === 'image',
  }))
  const groupBounds = nodesBounds(nodes)!

  beginCommit(origs.map((o) => o.id))

  // Dragging a text box's handle means the user wants an explicit width.
  batch(() => {
    for (const n of nodes) {
      if (n.type === 'text' && n.autoWidth) updateNode(n.id, { autoWidth: false })
    }
  })

  startDrag({
    onMove(ev) {
      const p = clientToWorld(ev)
      const single = origs.length === 1 ? origs[0]! : null

      // Images keep their aspect by default; shift releases the lock. Everything
      // else is the other way round.
      const aspect = single?.isImage ? !ev.shiftKey : ev.shiftKey
      const opts = { aspect, fromCenter: ev.altKey }

      if (single) {
        updateNode(single.id, resizeRotated(single.rect, single.rotation, handle, p, opts))
        return
      }

      const nextBounds = resizeRect(groupBounds, handle, p, opts)
      batch(() => {
        for (const o of origs) updateNode(o.id, mapRect(o.rect, groupBounds, nextBounds))
      })
    },
    onUp() {
      endCommit('Resize')
    },
    onCancel() {
      batch(() => {
        for (const o of origs) updateNode(o.id, o.rect)
      })
      abortCommit()
    },
  })
}

/**
 * Pivot for a multi-selection, in world space. Transient: a single node stores
 * its own, but a selection is not an object to store one on, so it resets to
 * the center whenever the selection changes.
 */
const [selectionPivot, setSelectionPivot] = createSignal<Point | null>(null)

function currentPivot(): Point | null {
  const nodes = selectedNodes()
  if (nodes.length === 1) return pivotToWorld(nodes[0]!)
  const b = nodesBounds(nodes)
  if (!b) return null
  return selectionPivot() ?? { x: b.x + b.w / 2, y: b.y + b.h / 2 }
}

function startRotate(e: PointerEvent): void {
  e.stopPropagation()
  e.preventDefault()

  const pivot = currentPivot()
  if (!pivot) return
  const origs = selectedNodes().map((n) => ({
    id: n.id,
    x: n.x,
    y: n.y,
    w: n.w,
    h: n.h,
    rotation: n.rotation,
  }))
  // Snapping applies to the angle turned, which for one node is also its absolute angle.
  const base = origs.length === 1 ? origs[0]!.rotation : 0
  const startAngle = angleTo(pivot, clientToWorld(e))
  if (origs.length > 1) setSelectionPivot(pivot)

  beginCommit(origs.map((o) => o.id))

  startDrag({
    onMove(ev) {
      let turned = base + (angleTo(pivot, clientToWorld(ev)) - startAngle)
      if (ev.shiftKey) turned = snapAngle(turned)
      const delta = turned - base
      batch(() => {
        for (const o of origs) updateNode(o.id, rotateAbout(o, pivot, delta))
      })
    },
    onUp() {
      endCommit('Rotate')
    },
    onCancel() {
      batch(() => {
        for (const o of origs) updateNode(o.id, { x: o.x, y: o.y, rotation: o.rotation })
      })
      abortCommit()
    },
  })
}

function snapPivot(p: Point, targets: Point[]): Point {
  const reach = PIVOT_SNAP / scene.camera.z
  return targets.find((t) => Math.hypot(t.x - p.x, t.y - p.y) < reach) ?? p
}

function startPivotDrag(e: PointerEvent): void {
  e.stopPropagation()
  e.preventDefault()

  const nodes = selectedNodes()
  if (nodes.length === 0) return

  if (nodes.length > 1) {
    const b = nodesBounds(nodes)!
    const targets = PIVOT_SNAPS.map((s) => ({ x: b.x + b.w * s.x, y: b.y + b.h * s.y }))
    const orig = selectionPivot()
    startDrag({
      onMove(ev) {
        const p = clientToWorld(ev)
        setSelectionPivot(ev.ctrlKey || ev.metaKey ? p : snapPivot(p, targets))
      },
      onCancel() {
        setSelectionPivot(orig)
      },
    })
    return
  }

  const node = nodes[0]!
  const orig = node.pivot
  const targets = PIVOT_SNAPS.map((s) => pivotToWorld({ ...node, pivot: s }))

  beginCommit([node.id])

  startDrag({
    onMove(ev) {
      const p = clientToWorld(ev)
      // Ctrl places it freely. Not Alt: Linux window managers take Alt+drag.
      updateNode(node.id, {
        pivot: pivotFromWorld(node, ev.ctrlKey || ev.metaKey ? p : snapPivot(p, targets)),
      })
    },
    onUp() {
      endCommit('Move pivot')
    },
    onCancel() {
      updateNode(node.id, { pivot: orig })
      abortCommit()
    },
  })
}

function resetPivot(e: MouseEvent): void {
  e.stopPropagation()
  const nodes = selectedNodes()
  if (nodes.length > 1) {
    setSelectionPivot(null)
    return
  }
  const node = nodes[0]
  if (!node?.pivot) return
  beginCommit([node.id])
  updateNode(node.id, { pivot: undefined })
  endCommit('Reset pivot')
}

export function SelectionOverlay() {
  const nodes = selectedNodes

  createEffect(on(selection, () => setSelectionPivot(null), { defer: true }))

  const visible = () =>
    nodes().length > 0 && activeTool() === 'select' && editingTextId() === null

  /** Frame corners in screen space: nw, ne, se, sw. */
  const corners = createMemo<Point[]>(() => {
    const sel = nodes()
    if (sel.length === 1) {
      return nodeCorners(sel[0]!).map((p) => worldToScreen(scene.camera, p))
    }
    const b = nodesBounds(sel)
    if (!b) return []
    const r = worldRectToScreen(scene.camera, b)
    return [
      { x: r.x, y: r.y },
      { x: r.x + r.w, y: r.y },
      { x: r.x + r.w, y: r.y + r.h },
      { x: r.x, y: r.y + r.h },
    ]
  })

  const handles = createMemo(() => (corners().length === 4 ? handlePositions(corners()) : null))

  const rotateHandle = createMemo(() => {
    const h = handles()
    if (!h) return null
    // Push out along the frame's own "up" direction so it follows rotation.
    const up = { x: h.n.x - h.s.x, y: h.n.y - h.s.y }
    const len = Math.hypot(up.x, up.y) || 1
    return { x: h.n.x + (up.x / len) * ROTATE_OFFSET, y: h.n.y + (up.y / len) * ROTATE_OFFSET }
  })

  const pivotHandle = createMemo(() => {
    const p = currentPivot()
    return p ? worldToScreen(scene.camera, p) : null
  })

  const marqueeScreen = createMemo(() => {
    const m = marquee()
    return m ? worldRectToScreen(scene.camera, m) : null
  })

  return (
    <svg class="overlay">
      <Show when={marqueeScreen()}>
        {(r) => (
          <rect
            class="marquee"
            x={r().x}
            y={r().y}
            width={r().w}
            height={r().h}
          />
        )}
      </Show>

      <Show when={visible() && corners().length === 4}>
        <polygon
          class="frame"
          points={corners().map((p) => `${p.x},${p.y}`).join(' ')}
        />

        <Show when={rotateHandle()}>
          {(p) => (
            <>
              <line
                class="frame"
                x1={handles()!.n.x}
                y1={handles()!.n.y}
                x2={p().x}
                y2={p().y}
              />
              <circle
                class="handle rotate"
                cx={p().x}
                cy={p().y}
                r={HANDLE_SIZE / 2}
                onPointerDown={startRotate}
              />
            </>
          )}
        </Show>

        <For each={HANDLES}>
          {(h) => (
            <rect
              class="handle"
              x={handles()![h].x - HANDLE_SIZE / 2}
              y={handles()![h].y - HANDLE_SIZE / 2}
              width={HANDLE_SIZE}
              height={HANDLE_SIZE}
              style={{ cursor: CURSORS[h] }}
              onPointerDown={(e) => startResize(h, e)}
            />
          )}
        </For>

        <Show when={pivotHandle()}>
          {(p) => (
            <g
              class="pivot"
              onPointerDown={startPivotDrag}
              onDblClick={resetPivot}
            >
              <circle cx={p().x} cy={p().y} r={PIVOT_RADIUS} />
              <path
                d={`M${p().x - PIVOT_RADIUS - 3} ${p().y}h${2 * PIVOT_RADIUS + 6}M${p().x} ${p().y - PIVOT_RADIUS - 3}v${2 * PIVOT_RADIUS + 6}`}
              />
            </g>
          )}
        </Show>
      </Show>
    </svg>
  )
}
